/**
 * ColumnLayout - Simple grid layout for content blocks
 * Replaces complex Grid usage with an easy-to-understand component
 */

import React from 'react'
import { Grid, Container } from '@mui/material'

interface ColumnLayoutProps {
	children: React.ReactNode[]
	columns?: 1 | 2 | 3 | 4
	spacing?: 'tight' | 'normal' | 'loose'
	align?: 'left' | 'center' | 'right'
	equalHeight?: boolean
	fullWidth?: boolean
}

const ColumnLayout: React.FC<ColumnLayoutProps> = ({
	children,
	columns = 3,
	spacing = 'normal',
	align = 'center',
	equalHeight = true,
	fullWidth = false
}) => {
	const getSpacing = () => {
		switch (spacing) {
			case 'tight':
				return 1
			case 'normal':
				return 2
			case 'loose':
				return 4
			default:
				return 2
		}
	}

	const getColumnSize = () => {
		switch (columns) {
			case 1:
				return 12
			case 2:
				return 6
			case 3:
				return 4
			case 4:
				return 3
			default:
				return 4
		}
	}

	const getJustifyContent = () => {
		switch (align) {
			case 'left':
				return 'flex-start'
			case 'center':
				return 'center'
			case 'right':
				return 'flex-end'
			default:
				return 'center'
		}
	}

	const content = (
		<Grid container spacing={getSpacing()} justifyContent={getJustifyContent()} alignItems={equalHeight ? 'stretch' : 'flex-start'}>
			{children.map((child, index) => (
				<Grid
					item
					xs={12}
					sm={columns > 1 ? 6 : 12}
					md={getColumnSize()}
					key={index}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: equalHeight ? '100%' : 'auto'
					}}
				>
					{child}
				</Grid>
			))}
		</Grid>
	)

	if (fullWidth) {
		return content
	}

	return <Container>{content}</Container>
}

export default ColumnLayout
